import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import {
  FaDownload as FaDownloadRaw,
  FaFileImport as FaFileImportRaw,
  FaFileExport as FaFileExportRaw,
  FaSearch as FaSearchRaw,
  FaSyncAlt as FaSyncAltRaw,
} from 'react-icons/fa';
import { useDonations, useDonationsSummary } from '../hooks/useDonations';
import { generateFinanceTaxStatements, getTaxStatementDownloadUrl, useFinanceTaxStatements } from '../hooks/useFinanceTaxStatements';
import { supabase } from '../services/supabase';
import { Table, TableWrap } from '../styles/marketingSharedStyles';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaDownload: any = FaDownloadRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaFileImport: any = FaFileImportRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaFileExport: any = FaFileExportRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaSearch: any = FaSearchRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaSyncAlt: any = FaSyncAltRaw;

type CsvContributionRow = {
  row_number: number;
  amount: number;
  currency: string;
  category: string;
  payment_method: string;
  status: string;
  created_at: string;
  donor_name: string | null;
  donor_email: string | null;
  user_id: string | null;
  notes: string | null;
};

type UserLookupRow = {
  id: string;
  full_name: string | null;
  email: string | null;
};

type ManualContributionDraftRow = {
  id: string;
  donorMode: 'existing' | 'new';
  userSearch: string;
  userResults: UserLookupRow[];
  selectedUserId: string;
  amount: string;
  createdDate: string;
  status: string;
  paymentMethod: string;
  donorName: string;
  donorEmail: string;
  notes: string;
};

type StatementDonorOption = {
  key: string;
  label: string;
  donorType: 'user' | 'email' | 'name';
  donorValue: string;
};

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  margin-top: 0.65rem;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;

const HeaderTitle = styled.h1`
  margin: 0;
  font-size: 1.45rem;
  color: var(--onSurface);
`;

const HeaderSubtitle = styled.p`
  margin: 0.35rem 0 0;
  color: var(--onSurfaceVariant);
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
`;

const ActionButton = styled.button<{ $primary?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  border: 1px solid ${({ $primary }) => ($primary ? 'var(--primary)' : 'var(--outline)')};
  background: ${({ $primary }) => ($primary ? 'var(--primary)' : 'var(--surface)')};
  color: ${({ $primary }) => ($primary ? 'var(--onPrimary)' : 'var(--onSurface)')};
  border-radius: 10px;
  padding: 0.55rem 0.85rem;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
  gap: 0.9rem;
`;

const Card = styled.section`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 12px;
  padding: 1rem 1.1rem;
`;

const CardLabel = styled.p`
  margin: 0;
  font-size: 0.78rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--muted);
`;

const CardValue = styled.h2`
  margin: 0.35rem 0 0;
  font-size: 1.5rem;
  color: var(--onSurface);
`;

const CardSub = styled.p`
  margin: 0.25rem 0 0;
  font-size: 0.8rem;
  color: var(--onSurfaceVariant);
`;

const PanelTitle = styled.h2`
  margin: 0 0 0.8rem;
  font-size: 1rem;
  color: var(--onSurface);
`;

const PanelSub = styled.p`
  margin: -0.35rem 0 0.8rem;
  color: var(--onSurfaceVariant);
  font-size: 0.82rem;
`;

const Input = styled.input`
  border: 1px solid var(--outline);
  border-radius: 8px;
  padding: 0.45rem 0.6rem;
  background: var(--surface);
  color: var(--onSurface);
  font-size: 0.85rem;
`;

const Select = styled.select`
  border: 1px solid var(--outline);
  border-radius: 8px;
  padding: 0.45rem 0.6rem;
  background: var(--surface);
  color: var(--onSurface);
  font-size: 0.85rem;
`;

const HelperText = styled.p`
  margin: 0.45rem 0 0;
  color: var(--onSurfaceVariant);
  font-size: 0.8rem;
`;

const InlineError = styled.p`
  margin: 0.45rem 0 0;
  color: var(--error);
  font-size: 0.8rem;
`;

const TwoColumn = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 1rem;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const TaxList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
`;

const TaxItem = styled.div`
  border: 1px solid color-mix(in srgb, var(--outline) 70%, transparent);
  border-radius: 14px;
  padding: 0.8rem 0.9rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.85rem;
  background: linear-gradient(180deg, color-mix(in srgb, var(--surface) 90%, white 10%) 0%, var(--surface) 100%);
  transition: border-color 120ms ease, transform 120ms ease;

  &:hover {
    border-color: color-mix(in srgb, var(--primary) 45%, var(--outline) 55%);
    transform: translateY(-1px);
  }
`;

const TaxMeta = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
`;

const TaxName = styled.p`
  margin: 0;
  font-size: 0.9rem;
  color: var(--onSurface);
  font-weight: 600;
`;

const TaxSub = styled.p`
  margin: 0;
  font-size: 0.8rem;
  color: var(--onSurfaceVariant);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TaxTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-bottom: 0.15rem;
`;

const TaxCountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  border: 1px solid color-mix(in srgb, var(--primary) 30%, var(--outline) 70%);
  background: color-mix(in srgb, var(--primary) 9%, transparent);
  color: var(--onSurface);
  border-radius: 999px;
  padding: 0.18rem 0.55rem;
  font-size: 0.74rem;
  font-weight: 600;
`;

const TaxBrowseSection = styled.section`
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid color-mix(in srgb, var(--outline) 70%, transparent);
`;

const TaxFormStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  width: 100%;
  margin-bottom: 0.5rem;
`;

const TaxTwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.65rem;
  width: 100%;
  min-width: 0;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const TaxControlSelect = styled(Select)`
  width: 100%;
  min-width: 0;
  height: 40px;
  box-sizing: border-box;
  border-radius: 10px;
`;

const TaxSearchInputWrap = styled.div`
  position: relative;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
`;

const TaxSearchInput = styled(Input)`
  width: 100%;
  min-width: 0;
  height: 40px;
  box-sizing: border-box;
  border-radius: 10px;
  padding-left: 1.9rem;
`;

const TaxGenerateButton = styled(ActionButton)`
  width: 100%;
  justify-content: center;
  min-height: 40px;
  box-sizing: border-box;
  border-radius: 10px;
`;

const TaxDonorSearchArea = styled.div`
  position: relative;
  width: 100%;
`;

const TaxDonorDropdown = styled.div`
  position: absolute;
  top: calc(100% + 0.35rem);
  left: 0;
  right: 0;
  z-index: 20;
  background: var(--surface);
  border: 1px solid color-mix(in srgb, var(--outline) 78%, transparent);
  border-radius: 10px;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.22);
  max-height: 220px;
  overflow-y: auto;
`;

const TaxDonorOptionButton = styled.button`
  width: 100%;
  text-align: left;
  border: none;
  background: transparent;
  color: var(--onSurface);
  padding: 0.55rem 0.7rem;
  font-size: 0.83rem;
  cursor: pointer;

  &:hover {
    background: color-mix(in srgb, var(--primary) 10%, transparent);
  }
`;

const TaxDonorDropdownLabel = styled.p`
  margin: 0;
  padding: 0.55rem 0.7rem;
  color: var(--onSurfaceVariant);
  font-size: 0.82rem;
`;

const GenerationTargetBanner = styled.div`
  margin-top: 0.1rem;
  border: 1px solid color-mix(in srgb, var(--primary) 38%, var(--outline) 62%);
  border-radius: 11px;
  background: linear-gradient(180deg, color-mix(in srgb, var(--primary) 14%, transparent) 0%, color-mix(in srgb, var(--surface) 94%, white 6%) 100%);
  padding: 0.55rem 0.7rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.55rem;
  flex-wrap: wrap;
`;

const GenerationTargetLabel = styled.span`
  font-size: 0.74rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--onSurfaceVariant);
  font-weight: 700;
`;

const GenerationTargetValue = styled.span`
  color: var(--onSurface);
  font-size: 0.86rem;
  font-weight: 700;
`;

const GenerationTargetMeta = styled.span`
  color: var(--onSurfaceVariant);
  font-size: 0.77rem;
`;

const GenerationTargetChips = styled.div`
  width: 100%;
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
`;

const GenerationTargetChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  border: 1px solid color-mix(in srgb, var(--primary) 38%, var(--outline) 62%);
  border-radius: 999px;
  background: color-mix(in srgb, var(--primary) 14%, transparent);
  color: var(--onSurface);
  font-size: 0.76rem;
  font-weight: 600;
  padding: 0.18rem 0.5rem;
  cursor: pointer;

  &:hover {
    background: color-mix(in srgb, var(--primary) 20%, transparent);
  }
`;

const GenerationTargetChipRemove = styled.span`
  font-size: 0.8rem;
  line-height: 1;
  opacity: 0.9;
`;

const TaxBrowseSearchRow = styled.div`
  width: 100%;
  margin-bottom: 0.65rem;
`;

const TaxSearchIcon = styled(FaSearch)`
  position: absolute;
  left: 0.6rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--onSurfaceVariant);
  pointer-events: none;
  font-size: 0.78rem;
`;

const Toast = styled.div`
  border: 1px solid var(--outline);
  border-radius: 10px;
  padding: 0.65rem 0.8rem;
  background: var(--surfaceVariant);
  color: var(--onSurface);
  font-size: 0.84rem;
`;

const DonorLink = styled(Link)`
  color: var(--primary);
  text-decoration: none;
  font-weight: 600;

  &:hover {
    text-decoration: underline;
  }
`;

const PaginationRow = styled.div`
  margin-top: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.6rem;
  flex-wrap: wrap;
`;

const PaginationActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
`;

const ManualRowsWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
`;

const ManualRowCard = styled.div`
  border: 1px solid var(--outline);
  border-radius: 10px;
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
`;

const RowGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 0.55rem;
`;

function formatCurrency(value: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(value);
}

function formatDate(value: string) {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function parseCsvLine(line: string): string[] {
  const cells: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (ch === ',' && !inQuotes) {
      cells.push(current.trim());
      current = '';
      continue;
    }
    current += ch;
  }
  cells.push(current.trim());
  return cells;
}

function escapeCsvValue(value: string | number | null | undefined): string {
  if (value == null) return '';
  const raw = String(value);
  const escaped = raw.replace(/"/g, '""');
  return /[",\n\r]/.test(escaped) ? `"${escaped}"` : escaped;
}

function parseContributionCsv(raw: string): { rows: CsvContributionRow[]; errors: string[] } {
  const lines = raw
    .replace(/^\uFEFF/, '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  if (!lines.length) {
    return { rows: [], errors: ['CSV is empty.'] };
  }

  const headers = parseCsvLine(lines[0]).map((h) => h.toLowerCase().replace(/\s+/g, '_'));
  const getIndex = (name: string) => headers.indexOf(name);
  const amountIndex = getIndex('amount');
  if (amountIndex < 0) {
    return { rows: [], errors: ['Missing required header: amount'] };
  }

  const rows: CsvContributionRow[] = [];
  const errors: string[] = [];
  for (let i = 1; i < lines.length; i += 1) {
    const rowNumber = i + 1;
    const cells = parseCsvLine(lines[i]);
    const getCell = (name: string) => {
      const idx = getIndex(name);
      return idx >= 0 ? (cells[idx] ?? '').trim() : '';
    };

    const amount = Number(getCell('amount'));
    if (!Number.isFinite(amount) || amount <= 0) {
      errors.push(`Row ${rowNumber}: amount must be a positive number.`);
      continue;
    }

    rows.push({
      row_number: rowNumber,
      amount,
      currency: (getCell('currency') || 'USD').toUpperCase(),
      category: getCell('category') || 'general',
      payment_method: getCell('payment_method') || 'manual',
      status: getCell('status') || 'completed',
      created_at: getCell('created_at') || new Date().toISOString(),
      donor_name: getCell('donor_name') || null,
      donor_email: getCell('donor_email') || null,
      user_id: getCell('user_id') || null,
      notes: getCell('notes') || null,
    });
  }

  return { rows, errors };
}

function createManualContributionDraftRow(): ManualContributionDraftRow {
  const now = new Date();
  const localIsoDate = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  const randomId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  return {
    id: randomId,
    donorMode: 'existing',
    userSearch: '',
    userResults: [],
    selectedUserId: '',
    amount: '',
    createdDate: localIsoDate,
    status: 'completed',
    paymentMethod: 'manual',
    donorName: '',
    donorEmail: '',
    notes: '',
  };
}

export default function FinancePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const pageSize = 10;
  const csvInputRef = useRef<HTMLInputElement | null>(null);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [csvRows, setCsvRows] = useState<CsvContributionRow[]>([]);
  const [csvErrors, setCsvErrors] = useState<string[]>([]);
  const [csvFileName, setCsvFileName] = useState<string | null>(null);
  const [csvParsing, setCsvParsing] = useState(false);
  const [csvUploading, setCsvUploading] = useState(false);
  const [paypalSyncing, setPaypalSyncing] = useState(false);
  const [squareSyncing, setSquareSyncing] = useState(false);
  const [manualRows, setManualRows] = useState<ManualContributionDraftRow[]>([createManualContributionDraftRow()]);
  const [manualSaving, setManualSaving] = useState(false);
  const [manualSaveMessage, setManualSaveMessage] = useState<string | null>(null);
  const [manualSearchingRowIds, setManualSearchingRowIds] = useState<string[]>([]);
  const [contributionDonorSearch, setContributionDonorSearch] = useState('');
  const [contributionDonorResults, setContributionDonorResults] = useState<StatementDonorOption[]>([]);
  const [contributionDonorSearching, setContributionDonorSearching] = useState(false);
  const [selectedContributionTarget, setSelectedContributionTarget] = useState<StatementDonorOption | null>(null);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [downloadStatementId, setDownloadStatementId] = useState<string | null>(null);
  const [taxSearch, setTaxSearch] = useState('');
  const [generateTaxYear, setGenerateTaxYear] = useState(String(new Date().getFullYear()));
  const [generateTaxMode, setGenerateTaxMode] = useState<'skip_existing' | 'replace_existing'>('skip_existing');
  const [generateTaxDonorSearch, setGenerateTaxDonorSearch] = useState('');
  const [generateTaxDonorResults, setGenerateTaxDonorResults] = useState<StatementDonorOption[]>([]);
  const [generateTaxSelectedDonors, setGenerateTaxSelectedDonors] = useState<StatementDonorOption[]>([]);
  const [generateTaxDonorSearching, setGenerateTaxDonorSearching] = useState(false);
  const [generatingTaxStatements, setGeneratingTaxStatements] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const manualSearchTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const lastManualSearchRef = useRef<Record<string, string>>({});
  const statementDonorSearchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastStatementDonorSearchRef = useRef('');
  const contributionDonorSearchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastContributionDonorSearchRef = useRef('');

  const donations = useDonations(true, {
    fromDate: fromDate || undefined,
    toDate: toDate || undefined,
    target: selectedContributionTarget
      ? { donorType: selectedContributionTarget.donorType, donorValue: selectedContributionTarget.donorValue }
      : undefined,
  });
  const summary = useDonationsSummary(true, { fromDate: fromDate || undefined, toDate: toDate || undefined });
  const taxStatements = useFinanceTaxStatements(true);

  const monthlyAverage = useMemo(() => {
    const weeks = summary.data?.byWeek ?? [];
    if (!weeks.length) return 0;
    const monthlyEquivalent = summary.data ? (summary.data.total / weeks.length) * 4 : 0;
    return monthlyEquivalent;
  }, [summary.data]);

  const thisMonthTotals = useQuery({
    queryKey: ['finance', 'this-month-total-non-pending'],
    queryFn: async () => {
      const now = new Date();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
      const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 1).toISOString();
      const { data, error } = await supabase
        .from('donations')
        .select('amount')
        .gte('created_at', monthStart)
        .lt('created_at', monthEnd)
        .not('status', 'in', '(pending,deleted,removed)');
      if (error) throw error;
      return (data ?? []).reduce((sum, row) => sum + Number(row.amount || 0), 0);
    },
  });

  const filteredTaxStatements = useMemo(() => {
    const list = taxStatements.data ?? [];
    const selectedYear = Number(generateTaxYear);
    return list.filter((item) => {
      const yearMatch = Number.isInteger(selectedYear) ? item.tax_year === selectedYear : true;
      if (!yearMatch) return false;
      if (!taxSearch.trim()) return true;
      const haystack = `${item.display_name ?? ''} ${item.file_name ?? ''} ${item.donor_name ?? ''} ${item.donor_email ?? ''}`.toLowerCase();
      return haystack.includes(taxSearch.trim().toLowerCase());
    });
  }, [generateTaxYear, taxSearch, taxStatements.data]);

  const availableTaxYears = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = new Set<number>([currentYear]);
    (taxStatements.data ?? []).forEach((row) => {
      if (Number.isInteger(row.tax_year)) years.add(row.tax_year);
    });
    return Array.from(years)
      .sort((a, b) => b - a)
      .map((year) => String(year));
  }, [taxStatements.data]);

  const contributionCategories = useMemo(
    () =>
      Array.from(new Set((donations.data ?? []).map((row) => row.category?.trim() || 'General'))).sort((a, b) =>
        a.localeCompare(b)
      ),
    [donations.data]
  );

  const filteredDonations = useMemo(() => {
    const rows = donations.data ?? [];
    if (categoryFilter === 'all') return rows;
    return rows.filter((row) => (row.category?.trim() || 'General') === categoryFilter);
  }, [categoryFilter, donations.data]);

  const paginatedDonations = useMemo(() => {
    const rows = filteredDonations;
    const start = (currentPage - 1) * pageSize;
    return rows.slice(start, start + pageSize);
  }, [currentPage, filteredDonations]);

  const totalRows = filteredDonations.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const showDonorSearchDropdown =
    generateTaxDonorSearch.trim().length >= 2 && (generateTaxDonorSearching || generateTaxDonorResults.length > 0);
  const showContributionDonorSearchDropdown =
    contributionDonorSearch.trim().length >= 2 && (contributionDonorSearching || contributionDonorResults.length > 0);

  useEffect(() => {
    setCurrentPage(1);
  }, [fromDate, toDate, categoryFilter, selectedContributionTarget?.key]);

  useEffect(() => {
    if (categoryFilter !== 'all' && !contributionCategories.includes(categoryFilter)) {
      setCategoryFilter('all');
    }
  }, [categoryFilter, contributionCategories]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const openCsvPicker = () => csvInputRef.current?.click();

  const searchStatementDonors = useCallback(async (search: string) => {
    const safeSearch = search.replace(/[,%()]/g, ' ').trim();
    if (safeSearch.length < 2) {
      setGenerateTaxDonorResults([]);
      setGenerateTaxDonorSearching(false);
      return;
    }

    setGenerateTaxDonorSearching(true);
    try {
      const [usersResp, donationsResp] = await Promise.all([
        supabase
          .from('users_directory')
          .select('id, full_name, email')
          .or(`full_name.ilike.%${safeSearch}%,email.ilike.%${safeSearch}%`)
          .limit(8),
        supabase
          .from('donations')
          .select('donor_name, donor_email')
          .or(`donor_name.ilike.%${safeSearch}%,donor_email.ilike.%${safeSearch}%`)
          .limit(20),
      ]);

      if (usersResp.error) throw usersResp.error;
      if (donationsResp.error) throw donationsResp.error;

      const byKey = new Map<string, StatementDonorOption>();

      const users = (usersResp.data ?? []) as UserLookupRow[];
      users.forEach((user) => {
        const key = `user:${user.id}`;
        const name = user.full_name?.trim() || user.email?.trim() || 'Unnamed member';
        const emailPart = user.email?.trim() ? ` - ${user.email.trim()}` : '';
        byKey.set(key, {
          key,
          label: `${name}${emailPart}`,
          donorType: 'user',
          donorValue: user.id,
        });
      });

      type DonationDonorRow = { donor_name: string | null; donor_email: string | null };
      const donationRows = (donationsResp.data ?? []) as DonationDonorRow[];
      donationRows.forEach((row) => {
        const email = row.donor_email?.trim().toLowerCase() || '';
        const name = row.donor_name?.trim() || '';
        if (email) {
          const key = `email:${email}`;
          if (!byKey.has(key)) {
            byKey.set(key, {
              key,
              label: `${name || email} - ${email}`,
              donorType: 'email',
              donorValue: email,
            });
          }
        } else if (name) {
          const key = `name:${name.toLowerCase()}`;
          if (!byKey.has(key)) {
            byKey.set(key, {
              key,
              label: name,
              donorType: 'name',
              donorValue: name,
            });
          }
        }
      });

      setGenerateTaxDonorResults(Array.from(byKey.values()).slice(0, 20));
    } catch (err) {
      setToastMessage(err instanceof Error ? err.message : 'Failed to search donors for statements.');
    } finally {
      setGenerateTaxDonorSearching(false);
    }
  }, []);

  const searchContributionDonors = useCallback(async (search: string) => {
    const safeSearch = search.replace(/[,%()]/g, ' ').trim();
    if (safeSearch.length < 2) {
      setContributionDonorResults([]);
      setContributionDonorSearching(false);
      return;
    }

    setContributionDonorSearching(true);
    try {
      const [usersResp, donationsResp] = await Promise.all([
        supabase
          .from('users_directory')
          .select('id, full_name, email')
          .or(`full_name.ilike.%${safeSearch}%,email.ilike.%${safeSearch}%`)
          .limit(10),
        supabase
          .from('donations')
          .select('donor_name, donor_email')
          .or(`donor_name.ilike.%${safeSearch}%,donor_email.ilike.%${safeSearch}%`)
          .limit(25),
      ]);
      if (usersResp.error) throw usersResp.error;
      if (donationsResp.error) throw donationsResp.error;

      const byKey = new Map<string, StatementDonorOption>();

      const users = (usersResp.data ?? []) as UserLookupRow[];
      users.forEach((user) => {
        const key = `user:${user.id}`;
        const name = user.full_name?.trim() || user.email?.trim() || 'Unnamed member';
        const emailPart = user.email?.trim() ? ` - ${user.email.trim()}` : '';
        byKey.set(key, {
          key,
          label: `${name}${emailPart}`,
          donorType: 'user',
          donorValue: user.id,
        });
      });

      type DonationDonorRow = { donor_name: string | null; donor_email: string | null };
      const donationRows = (donationsResp.data ?? []) as DonationDonorRow[];
      donationRows.forEach((row) => {
        const email = row.donor_email?.trim().toLowerCase() || '';
        const name = row.donor_name?.trim() || '';
        if (email) {
          const key = `email:${email}`;
          if (!byKey.has(key)) {
            byKey.set(key, {
              key,
              label: `${name || email} - ${email}`,
              donorType: 'email',
              donorValue: email,
            });
          }
        } else if (name) {
          const key = `name:${name.toLowerCase()}`;
          if (!byKey.has(key)) {
            byKey.set(key, {
              key,
              label: name,
              donorType: 'name',
              donorValue: name,
            });
          }
        }
      });

      setContributionDonorResults(Array.from(byKey.values()).slice(0, 25));
    } catch (err) {
      setToastMessage(err instanceof Error ? err.message : 'Failed to search donors for contributions.');
    } finally {
      setContributionDonorSearching(false);
    }
  }, []);

  const updateManualRow = useCallback((rowId: string, patch: Partial<ManualContributionDraftRow>) => {
    setManualRows((rows) => rows.map((row) => (row.id === rowId ? { ...row, ...patch } : row)));
  }, []);

  const addManualRow = () => {
    setManualRows((rows) => [...rows, createManualContributionDraftRow()]);
  };

  const removeManualRow = (rowId: string) => {
    setManualRows((rows) => {
      if (rows.length <= 1) return rows;
      return rows.filter((row) => row.id !== rowId);
    });
  };

  const setManualRowSearching = (rowId: string, searching: boolean) => {
    setManualSearchingRowIds((rows) => {
      if (searching) {
        return rows.includes(rowId) ? rows : [...rows, rowId];
      }
      return rows.filter((id) => id !== rowId);
    });
  };

  const searchUsersForRow = useCallback(async (rowId: string, search: string) => {
    setManualRowSearching(rowId, true);
    try {
      const safeSearch = search.replace(/[,%()]/g, ' ').trim();
      const { data, error } = await supabase
        .from('users_directory')
        .select('id, full_name, email')
        .or(`full_name.ilike.%${safeSearch}%,email.ilike.%${safeSearch}%`)
        .limit(10);
      if (error) throw error;
      const results = (data ?? []) as UserLookupRow[];
      // Keep explicit user selection stable; search refresh should not auto-replace it.
      updateManualRow(rowId, { userResults: results });
    } catch (err) {
      setToastMessage(err instanceof Error ? err.message : 'Failed to search users.');
    } finally {
      setManualRowSearching(rowId, false);
    }
  }, [updateManualRow]);

  useEffect(() => {
    const activeRowIds = new Set(manualRows.map((row) => row.id));

    Object.entries(manualSearchTimersRef.current).forEach(([rowId, timer]) => {
      if (!activeRowIds.has(rowId)) {
        clearTimeout(timer);
        delete manualSearchTimersRef.current[rowId];
      }
    });
    Object.keys(lastManualSearchRef.current).forEach((rowId) => {
      if (!activeRowIds.has(rowId)) {
        delete lastManualSearchRef.current[rowId];
      }
    });

    manualRows.forEach((row) => {
      if (row.donorMode !== 'existing') {
        if (manualSearchTimersRef.current[row.id]) {
          clearTimeout(manualSearchTimersRef.current[row.id]);
          delete manualSearchTimersRef.current[row.id];
        }
        delete lastManualSearchRef.current[row.id];
        return;
      }

      const query = row.userSearch.trim();
      if (query.length < 2) {
        if (manualSearchTimersRef.current[row.id]) {
          clearTimeout(manualSearchTimersRef.current[row.id]);
          delete manualSearchTimersRef.current[row.id];
        }
        delete lastManualSearchRef.current[row.id];
        if (row.userResults.length || row.selectedUserId) {
          updateManualRow(row.id, { userResults: [], selectedUserId: '' });
        }
        return;
      }

      if (lastManualSearchRef.current[row.id] === query) return;
      if (manualSearchTimersRef.current[row.id]) {
        clearTimeout(manualSearchTimersRef.current[row.id]);
      }
      manualSearchTimersRef.current[row.id] = setTimeout(() => {
        lastManualSearchRef.current[row.id] = query;
        void searchUsersForRow(row.id, query);
      }, 350);
    });
    return () => {
      Object.values(manualSearchTimersRef.current).forEach((timer) => clearTimeout(timer));
      manualSearchTimersRef.current = {};
    };
  }, [manualRows, searchUsersForRow, updateManualRow]);

  useEffect(() => {
    const query = generateTaxDonorSearch.trim();
    if (query.length < 2) {
      if (statementDonorSearchTimerRef.current) {
        clearTimeout(statementDonorSearchTimerRef.current);
        statementDonorSearchTimerRef.current = null;
      }
      lastStatementDonorSearchRef.current = '';
      setGenerateTaxDonorResults([]);
      setGenerateTaxDonorSearching(false);
      return;
    }
    if (lastStatementDonorSearchRef.current === query) return;

    if (statementDonorSearchTimerRef.current) {
      clearTimeout(statementDonorSearchTimerRef.current);
    }
    statementDonorSearchTimerRef.current = setTimeout(() => {
      lastStatementDonorSearchRef.current = query;
      void searchStatementDonors(query);
    }, 300);

    return () => {
      if (statementDonorSearchTimerRef.current) {
        clearTimeout(statementDonorSearchTimerRef.current);
        statementDonorSearchTimerRef.current = null;
      }
    };
  }, [generateTaxDonorSearch, searchStatementDonors]);

  useEffect(() => {
    const query = contributionDonorSearch.trim();
    if (query.length < 2) {
      if (contributionDonorSearchTimerRef.current) {
        clearTimeout(contributionDonorSearchTimerRef.current);
        contributionDonorSearchTimerRef.current = null;
      }
      lastContributionDonorSearchRef.current = '';
      setContributionDonorResults([]);
      setContributionDonorSearching(false);
      return;
    }
    if (lastContributionDonorSearchRef.current === query) return;

    if (contributionDonorSearchTimerRef.current) {
      clearTimeout(contributionDonorSearchTimerRef.current);
    }
    contributionDonorSearchTimerRef.current = setTimeout(() => {
      lastContributionDonorSearchRef.current = query;
      void searchContributionDonors(query);
    }, 300);

    return () => {
      if (contributionDonorSearchTimerRef.current) {
        clearTimeout(contributionDonorSearchTimerRef.current);
        contributionDonorSearchTimerRef.current = null;
      }
    };
  }, [contributionDonorSearch, searchContributionDonors]);

  const handleSaveManualBulkRows = async () => {
    if (!manualRows.length) {
      setManualSaveMessage('Add at least one row.');
      return;
    }

    const preparedRows: CsvContributionRow[] = [];
    const errors: string[] = [];
    manualRows.forEach((row, index) => {
      const rowNumber = index + 1;
      const amount = Number(row.amount);
      if (!Number.isFinite(amount) || amount <= 0) {
        errors.push(`Row ${rowNumber}: amount must be greater than 0.`);
        return;
      }

      const createdAt = row.createdDate ? new Date(`${row.createdDate}T12:00:00.000Z`).toISOString() : new Date().toISOString();
      const status = row.status || 'completed';
      const paymentMethod = row.paymentMethod || 'manual';
      let userId: string | null = null;
      let donorName: string | null = null;
      let donorEmail: string | null = null;

      if (row.donorMode === 'existing') {
        if (!row.selectedUserId) {
          errors.push(`Row ${rowNumber}: select a member result.`);
          return;
        }
        userId = row.selectedUserId;
      } else {
        donorName = row.donorName.trim() || null;
        donorEmail = row.donorEmail.trim().toLowerCase() || null;
        if (!donorName && !donorEmail) {
          errors.push(`Row ${rowNumber}: enter donor name or donor email.`);
          return;
        }
      }

      preparedRows.push({
        row_number: rowNumber,
        amount: Number(amount.toFixed(2)),
        currency: 'USD',
        category: 'in-house',
        payment_method: paymentMethod,
        status,
        created_at: createdAt,
        donor_name: donorName,
        donor_email: donorEmail,
        user_id: userId,
        notes: row.notes.trim() || null,
      });
    });

    if (errors.length > 0) {
      setManualSaveMessage(errors.slice(0, 4).join(' '));
      return;
    }

    setManualSaving(true);
    setManualSaveMessage(null);
    try {
      const { data, error } = await supabase.functions.invoke('finance-import-contributions', {
        body: { rows: preparedRows },
      });
      if (error) throw error;
      const payload = (data ?? {}) as { created?: number; failed?: number; error?: string };
      if (payload.error) throw new Error(payload.error);
      await queryClient.invalidateQueries({ queryKey: ['dashboard', 'donations'] });
      await queryClient.invalidateQueries({ queryKey: ['dashboard', 'donations-summary'] });
      setManualSaveMessage(`In-house rows saved. Created ${payload.created ?? 0}, failed ${payload.failed ?? 0}.`);
      setManualRows([createManualContributionDraftRow()]);
    } catch (err) {
      setManualSaveMessage(err instanceof Error ? err.message : 'Manual bulk save failed.');
    } finally {
      setManualSaving(false);
    }
  };

  const handleCsvChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setCsvFileName(file.name);
    setCsvParsing(true);
    setToastMessage(null);
    const reader = new FileReader();
    reader.onload = () => {
      const text = typeof reader.result === 'string' ? reader.result : '';
      const parsed = parseContributionCsv(text);
      setCsvRows(parsed.rows);
      setCsvErrors(parsed.errors);
      setCsvParsing(false);
    };
    reader.onerror = () => {
      setCsvRows([]);
      setCsvErrors(['Failed to read CSV file.']);
      setCsvParsing(false);
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  const handleImportCsv = async () => {
    if (!csvRows.length || csvErrors.length > 0) return;
    setCsvUploading(true);
    setToastMessage(null);
    try {
      const { data, error } = await supabase.functions.invoke('finance-import-contributions', {
        body: { rows: csvRows },
      });
      if (error) throw error;
      const payload = (data ?? {}) as { created?: number; failed?: number; error?: string };
      if (payload.error) throw new Error(payload.error);
      await queryClient.invalidateQueries({ queryKey: ['dashboard', 'donations'] });
      await queryClient.invalidateQueries({ queryKey: ['dashboard', 'donations-summary'] });
      setToastMessage(`Import complete. Created ${payload.created ?? 0}, failed ${payload.failed ?? 0}.`);
    } catch (err) {
      setToastMessage(err instanceof Error ? err.message : 'Import failed.');
    } finally {
      setCsvUploading(false);
    }
  };

  const handleSyncPaypal = async () => {
    setPaypalSyncing(true);
    setToastMessage(null);
    try {
      const { data, error } = await supabase.functions.invoke('finance-sync-paypal-contributions', {
        body: {
          ...(fromDate ? { startDate: `${fromDate}T00:00:00.000Z` } : {}),
          ...(toDate ? { endDate: `${toDate}T23:59:59.999Z` } : {}),
        },
      });
      if (error) throw error;
      const payload = (data ?? {}) as { synced?: number; processed?: number; skipped?: number; error?: string };
      if (payload.error) throw new Error(payload.error);
      await queryClient.invalidateQueries({ queryKey: ['dashboard', 'donations'] });
      await queryClient.invalidateQueries({ queryKey: ['dashboard', 'donations-summary'] });
      setToastMessage(
        `PayPal sync complete. Synced ${payload.synced ?? 0}, processed ${payload.processed ?? 0}, skipped ${payload.skipped ?? 0}.`
      );
    } catch (err) {
      setToastMessage(err instanceof Error ? err.message : 'PayPal sync failed.');
    } finally {
      setPaypalSyncing(false);
    }
  };

  const handleSyncSquare = async () => {
    setSquareSyncing(true);
    setToastMessage(null);
    try {
      const { data, error } = await supabase.functions.invoke('finance-sync-square-contributions', {
        body: {
          ...(fromDate ? { startDate: `${fromDate}T00:00:00.000Z` } : {}),
          ...(toDate ? { endDate: `${toDate}T23:59:59.999Z` } : {}),
        },
      });
      if (error) throw error;
      const payload = (data ?? {}) as { synced?: number; processed?: number; skipped?: number; error?: string };
      if (payload.error) throw new Error(payload.error);
      await queryClient.invalidateQueries({ queryKey: ['dashboard', 'donations'] });
      await queryClient.invalidateQueries({ queryKey: ['dashboard', 'donations-summary'] });
      setToastMessage(
        `Square sync complete. Synced ${payload.synced ?? 0}, processed ${payload.processed ?? 0}, skipped ${payload.skipped ?? 0}.`
      );
    } catch (err) {
      setToastMessage(err instanceof Error ? err.message : 'Square sync failed.');
    } finally {
      setSquareSyncing(false);
    }
  };

  const handleExportCsv = () => {
    const rows = donations.data ?? [];
    const header = ['donor_name', 'donor_email', 'category', 'date', 'amount', 'currency', 'status', 'payment_method'];
    const csv = [
      header.join(','),
      ...rows.map((row) =>
        [
          escapeCsvValue(row.user?.full_name || row.donor_name || 'Unknown'),
          escapeCsvValue(row.user?.email || row.donor_email || ''),
          escapeCsvValue(row.category || ''),
          escapeCsvValue(formatDate(row.created_at)),
          escapeCsvValue(Number(row.amount || 0).toFixed(2)),
          escapeCsvValue(row.currency || 'USD'),
          escapeCsvValue(row.status || ''),
          escapeCsvValue(row.payment_method || ''),
        ].join(',')
      ),
    ].join('\r\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `finance-contributions-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleDownloadStatement = async (statementId: string) => {
    setDownloadStatementId(statementId);
    setToastMessage(null);
    try {
      const { url, fileName } = await getTaxStatementDownloadUrl(statementId);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.target = '_blank';
      link.rel = 'noreferrer';
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setToastMessage(err instanceof Error ? err.message : 'Failed to download statement.');
    } finally {
      setDownloadStatementId(null);
    }
  };

  const handleGenerateTaxStatements = async () => {
    const year = Number(generateTaxYear);
    if (!Number.isInteger(year) || year < 1900 || year > 9999) {
      setToastMessage('Enter a valid 4-digit year.');
      return;
    }
    setGeneratingTaxStatements(true);
    setToastMessage(null);
    try {
      let result:
        | {
            taxYear: number;
            created: number;
            replaced: number;
            skipped: number;
            failed: number;
            errors: string[];
          }
        | null = null;

      if (generateTaxSelectedDonors.length > 0) {
        const aggregate = {
          taxYear: year,
          created: 0,
          replaced: 0,
          skipped: 0,
          failed: 0,
          errors: [] as string[],
        };
        for (const target of generateTaxSelectedDonors) {
          const donorResult = await generateFinanceTaxStatements(year, generateTaxMode, {
            donorType: target.donorType,
            donorValue: target.donorValue,
          });
          aggregate.created += donorResult.created;
          aggregate.replaced += donorResult.replaced;
          aggregate.skipped += donorResult.skipped;
          aggregate.failed += donorResult.failed;
          aggregate.errors.push(...donorResult.errors);
        }
        result = aggregate;
      } else {
        const globalResult = await generateFinanceTaxStatements(year, generateTaxMode);
        result = {
          taxYear: globalResult.taxYear,
          created: globalResult.created,
          replaced: globalResult.replaced,
          skipped: globalResult.skipped,
          failed: globalResult.failed,
          errors: globalResult.errors,
        };
      }

      await queryClient.invalidateQueries({ queryKey: ['finance', 'tax-statements'] });
      const donorMessage =
        generateTaxSelectedDonors.length > 0
          ? ` for ${generateTaxSelectedDonors.length} selected target${generateTaxSelectedDonors.length === 1 ? '' : 's'}`
          : '';
      const message = `Generation complete for ${result.taxYear}${donorMessage}. Created ${result.created}, replaced ${result.replaced}, skipped ${result.skipped}, failed ${result.failed}.`;
      const errorMessage = result.errors.length > 0 ? ` ${result.errors.slice(0, 2).join(' ')}` : '';
      setToastMessage(`${message}${errorMessage}`);
    } catch (err) {
      setToastMessage(err instanceof Error ? err.message : 'Failed to generate tax statements.');
    } finally {
      setGeneratingTaxStatements(false);
    }
  };

  return (
    <Page>
      <Header>
        <div>
          <HeaderTitle>Finance</HeaderTitle>
          <HeaderSubtitle>Track contribution activity, tax statements, CSV operations, and PayPal/Square sync.</HeaderSubtitle>
        </div>
        <HeaderActions>
          <input ref={csvInputRef} type="file" accept=".csv,text/csv" style={{ display: 'none' }} onChange={handleCsvChange} />
          <ActionButton type="button" onClick={handleSyncPaypal} disabled={paypalSyncing}>
            <FaSyncAlt size={14} />
            {paypalSyncing ? 'Syncing PayPal…' : 'Sync PayPal'}
          </ActionButton>
          <ActionButton type="button" onClick={handleSyncSquare} disabled={squareSyncing}>
            <FaSyncAlt size={14} />
            {squareSyncing ? 'Syncing Square…' : 'Sync Square'}
          </ActionButton>
          <ActionButton type="button" onClick={openCsvPicker}>
            <FaFileImport size={14} />
            Import CSV
          </ActionButton>
          <ActionButton type="button" onClick={handleExportCsv} $primary disabled={(donations.data?.length ?? 0) === 0}>
            <FaFileExport size={14} />
            Export CSV
          </ActionButton>
          <Input type="date" value={fromDate} onChange={(event) => setFromDate(event.target.value)} />
          <Input type="date" value={toDate} onChange={(event) => setToDate(event.target.value)} />
        </HeaderActions>
      </Header>

      {toastMessage ? <Toast role="status">{toastMessage}</Toast> : null}

      <Grid>
        <Card>
          <CardLabel>Total Contributions (range)</CardLabel>
          <CardValue>{formatCurrency(summary.data?.total ?? 0)}</CardValue>
          <CardSub>{(summary.data?.byWeek?.length ?? 0) > 0 ? `${summary.data?.byWeek.length ?? 0} weekly buckets` : 'No activity in selected range'}</CardSub>
        </Card>
        <Card>
          <CardLabel>Monthly Average</CardLabel>
          <CardValue>{formatCurrency(monthlyAverage)}</CardValue>
          <CardSub>Derived from weekly totals</CardSub>
        </Card>
        <Card>
          <CardLabel>This Month</CardLabel>
          <CardValue>{formatCurrency(thisMonthTotals.data ?? 0)}</CardValue>
          <CardSub>Current calendar month (non-pending)</CardSub>
        </Card>
        <Card>
          <CardLabel>Transactions Loaded</CardLabel>
          <CardValue>{donations.data?.length ?? 0}</CardValue>
          <CardSub>Most recent 200 rows{selectedContributionTarget ? ' for selected donor' : ''}</CardSub>
        </Card>
      </Grid>

      <Card>
        <PanelTitle>Manual In-House Contributions (Bulk Rows)</PanelTitle>
        <PanelSub>
          Add one or more rows, choose member or new donor, then save as a single import. Category is locked to in-house.
        </PanelSub>
        {manualSaveMessage ? <Toast role="status">{manualSaveMessage}</Toast> : null}
        <ManualRowsWrap>
          {manualRows.map((row, index) => (
            <ManualRowCard key={row.id}>
              <HelperText>Row {index + 1}</HelperText>
              <RowGrid>
                <Select
                  value={row.donorMode}
                  onChange={(event) =>
                    updateManualRow(row.id, {
                      donorMode: event.target.value === 'new' ? 'new' : 'existing',
                      userSearch: '',
                      userResults: [],
                      selectedUserId: '',
                    })
                  }
                >
                  <option value="existing">Existing member</option>
                  <option value="new">New donor</option>
                </Select>
                <Input
                  type="number"
                  min="0.01"
                  step="0.01"
                  placeholder="Amount (USD)"
                  value={row.amount}
                  onChange={(event) => updateManualRow(row.id, { amount: event.target.value })}
                />
                <Input
                  type="date"
                  value={row.createdDate}
                  onChange={(event) => updateManualRow(row.id, { createdDate: event.target.value })}
                />
                <Select value={row.status} onChange={(event) => updateManualRow(row.id, { status: event.target.value })}>
                  <option value="completed">completed</option>
                  <option value="pending">pending</option>
                  <option value="failed">failed</option>
                  <option value="refunded">refunded</option>
                </Select>
                <Select
                  value={row.paymentMethod}
                  onChange={(event) => updateManualRow(row.id, { paymentMethod: event.target.value })}
                >
                  <option value="manual">manual</option>
                  <option value="cash">cash</option>
                  <option value="bank_transfer">bank_transfer</option>
                  <option value="cheque">cheque</option>
                </Select>
                <Input
                  type="text"
                  placeholder="Optional notes"
                  value={row.notes}
                  onChange={(event) => updateManualRow(row.id, { notes: event.target.value })}
                />
              </RowGrid>

              {row.donorMode === 'existing' ? (
                <>
                  <TaxDonorSearchArea>
                    <TaxSearchInputWrap>
                      <TaxSearchIcon />
                      <TaxSearchInput
                        type="search"
                        placeholder="Search member by name or email"
                        value={row.userSearch}
                        onChange={(event) =>
                          updateManualRow(row.id, {
                            userSearch: event.target.value,
                            selectedUserId: '',
                          })
                        }
                      />
                    </TaxSearchInputWrap>
                    {row.userSearch.trim().length >= 2 && (manualSearchingRowIds.includes(row.id) || row.userResults.length > 0) ? (
                      <TaxDonorDropdown>
                        {manualSearchingRowIds.includes(row.id) ? <TaxDonorDropdownLabel>Searching members...</TaxDonorDropdownLabel> : null}
                        {!manualSearchingRowIds.includes(row.id) && row.userResults.length === 0 ? (
                          <TaxDonorDropdownLabel>No members found.</TaxDonorDropdownLabel>
                        ) : null}
                        {!manualSearchingRowIds.includes(row.id)
                          ? row.userResults.map((user) => (
                              <TaxDonorOptionButton
                                key={user.id}
                                type="button"
                                onClick={() => {
                                  const label = `${user.full_name || 'Unnamed'} - ${user.email || 'no-email'}`;
                                  updateManualRow(row.id, {
                                    selectedUserId: user.id,
                                    userSearch: label,
                                    userResults: [],
                                  });
                                }}
                              >
                                {(user.full_name || 'Unnamed')} - {user.email || 'no-email'}
                              </TaxDonorOptionButton>
                            ))
                          : null}
                      </TaxDonorDropdown>
                    ) : null}
                  </TaxDonorSearchArea>
                  <HelperText>
                    {manualSearchingRowIds.includes(row.id)
                      ? 'Searching members...'
                      : row.selectedUserId
                      ? 'Member selected.'
                      : row.userSearch.trim().length > 0 && row.userSearch.trim().length < 2
                      ? 'Type at least 2 characters to search.'
                      : row.userSearch.trim().length >= 2 && row.userResults.length === 0
                      ? 'No members found.'
                      : row.userResults.length > 0
                      ? `${row.userResults.length} member result(s).`
                      : 'Start typing to search members.'}
                  </HelperText>
                </>
              ) : (
                <RowGrid>
                  <Input
                    type="text"
                    placeholder="New donor name"
                    value={row.donorName}
                    onChange={(event) => updateManualRow(row.id, { donorName: event.target.value })}
                  />
                  <Input
                    type="email"
                    placeholder="New donor email"
                    value={row.donorEmail}
                    onChange={(event) => updateManualRow(row.id, { donorEmail: event.target.value })}
                  />
                </RowGrid>
              )}

              <HeaderActions>
                <ActionButton type="button" onClick={() => removeManualRow(row.id)} disabled={manualRows.length <= 1}>
                  Remove row
                </ActionButton>
              </HeaderActions>
            </ManualRowCard>
          ))}
        </ManualRowsWrap>
        <HeaderActions style={{ marginTop: '0.8rem' }}>
          <ActionButton type="button" onClick={addManualRow}>
            Add row
          </ActionButton>
          <ActionButton type="button" $primary onClick={handleSaveManualBulkRows} disabled={manualSaving}>
            {manualSaving ? 'Saving rows…' : 'Save In-House Rows'}
          </ActionButton>
        </HeaderActions>
      </Card>

      {csvFileName ? (
        <Card>
          <PanelTitle>CSV Import Preview</PanelTitle>
          <PanelSub>{csvFileName}</PanelSub>
          {csvParsing ? <HelperText>Parsing CSV…</HelperText> : null}
          {!csvParsing ? <HelperText>Parsed {csvRows.length} contribution rows.</HelperText> : null}
          {csvErrors.length > 0 ? <InlineError>{csvErrors.slice(0, 3).join(' ')}</InlineError> : null}
          <HeaderActions style={{ marginTop: '0.6rem' }}>
            <ActionButton type="button" onClick={handleImportCsv} $primary disabled={csvUploading || csvRows.length === 0 || csvErrors.length > 0}>
              {csvUploading ? 'Importing…' : 'Run Import'}
            </ActionButton>
          </HeaderActions>
        </Card>
      ) : null}

      <TwoColumn>
        <Card>
          <PanelTitle>Recent Contributions</PanelTitle>
          <PanelSub>
            Search and select a donor (member or non-member) to load only their contributions.
          </PanelSub>
          <TaxDonorSearchArea style={{ marginBottom: '0.7rem' }}>
            <TaxSearchInputWrap>
              <TaxSearchIcon />
              <TaxSearchInput
                type="search"
                placeholder="Search donor by name or email"
                value={contributionDonorSearch}
                onChange={(event) => setContributionDonorSearch(event.target.value)}
              />
            </TaxSearchInputWrap>
            {showContributionDonorSearchDropdown ? (
              <TaxDonorDropdown>
                <TaxDonorOptionButton
                  type="button"
                  onClick={() => {
                    setSelectedContributionTarget(null);
                    setContributionDonorSearch('');
                  }}
                >
                  All donors
                </TaxDonorOptionButton>
                {contributionDonorSearching ? <TaxDonorDropdownLabel>Searching donors...</TaxDonorDropdownLabel> : null}
                {!contributionDonorSearching && contributionDonorResults.length === 0 ? (
                  <TaxDonorDropdownLabel>No donors found.</TaxDonorDropdownLabel>
                ) : null}
                {!contributionDonorSearching
                  ? contributionDonorResults.map((option) => (
                      <TaxDonorOptionButton
                        key={option.key}
                        type="button"
                        onClick={() => {
                          setSelectedContributionTarget(option);
                          setContributionDonorSearch('');
                        }}
                      >
                        {option.label}
                      </TaxDonorOptionButton>
                    ))
                  : null}
              </TaxDonorDropdown>
            ) : null}
          </TaxDonorSearchArea>
          <HeaderActions style={{ marginBottom: '0.55rem' }}>
            <HelperText>
              {contributionDonorSearching
                ? 'Searching donors...'
                : contributionDonorSearch.trim().length > 0 && contributionDonorSearch.trim().length < 2
                ? 'Type at least 2 characters to search.'
                : contributionDonorSearch.trim().length >= 2 && contributionDonorResults.length === 0
                ? 'No donors found.'
                : selectedContributionTarget
                ? `Filtered donor: ${selectedContributionTarget.label}`
                : 'Showing all donors.'}
            </HelperText>
            <Select value={categoryFilter} onChange={(event) => setCategoryFilter(event.target.value)}>
              <option value="all">All categories</option>
              {contributionCategories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </Select>
            {selectedContributionTarget ? (
              <ActionButton
                type="button"
                onClick={() => {
                  setSelectedContributionTarget(null);
                }}
              >
                Clear donor filter
              </ActionButton>
            ) : null}
          </HeaderActions>
          {donations.isLoading ? <HelperText>Loading contributions…</HelperText> : null}
          {donations.error ? <InlineError>Failed to load contributions.</InlineError> : null}
          {!donations.isLoading && !donations.error ? (
            <TableWrap>
              <Table $mobileCards>
                <thead>
                  <tr>
                    <th>Donor</th>
                    <th>Category</th>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Method</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedDonations.map((row) => (
                    <tr key={row.id}>
                      <td data-label="Donor">
                        {row.user_id ? (
                          <DonorLink to={`/members/${row.user_id}`}>
                            {row.user?.full_name || row.donor_name || row.user?.email || row.donor_email || 'Unknown donor'}
                          </DonorLink>
                        ) : (
                          row.user?.full_name || row.donor_name || row.user?.email || row.donor_email || 'Unknown donor'
                        )}
                      </td>
                      <td data-label="Category">{row.category || 'General'}</td>
                      <td data-label="Date">{formatDate(row.created_at)}</td>
                      <td data-label="Amount">{formatCurrency(Number(row.amount || 0))}</td>
                      <td data-label="Status">{row.status || 'unknown'}</td>
                      <td data-label="Method">{row.payment_method || 'manual'}</td>
                      <td data-label="Actions">
                        {(() => {
                          const donorLabel =
                            row.user?.full_name ||
                            row.donor_name ||
                            row.user?.email ||
                            row.donor_email ||
                            'Unknown donor';
                          const target =
                            row.user_id
                              ? `/finance/donor/user/${encodeURIComponent(row.user_id)}`
                              : row.donor_email
                              ? `/finance/donor/email/${encodeURIComponent(row.donor_email)}`
                              : row.donor_name
                              ? `/finance/donor/name/${encodeURIComponent(row.donor_name)}`
                              : null;
                          return (
                        <ActionButton
                          type="button"
                          onClick={() =>
                            target
                              ? navigate(target, {
                                  state: { donorLabel },
                                })
                              : undefined
                          }
                          disabled={!target}
                        >
                          View donor
                        </ActionButton>
                          );
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
          ) : null}
          {!donations.isLoading && !donations.error ? (
            <PaginationRow>
              <HelperText>
                Page {currentPage} of {totalPages} ({totalRows} rows)
              </HelperText>
              <PaginationActions>
                <ActionButton type="button" onClick={() => setCurrentPage((page) => Math.max(1, page - 1))} disabled={currentPage <= 1}>
                  Previous
                </ActionButton>
                <ActionButton type="button" onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))} disabled={currentPage >= totalPages}>
                  Next
                </ActionButton>
              </PaginationActions>
            </PaginationRow>
          ) : null}
        </Card>

        <Card>
          <TaxTitleRow>
            <PanelTitle style={{ margin: 0 }}>Tax Statements</PanelTitle>
            <TaxCountBadge>{filteredTaxStatements.length} shown</TaxCountBadge>
          </TaxTitleRow>
          <TaxFormStack>
            <TaxTwoCol>
              <TaxControlSelect value={generateTaxYear} onChange={(event) => setGenerateTaxYear(event.target.value)}>
                {availableTaxYears.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </TaxControlSelect>
              <TaxControlSelect value={generateTaxMode} onChange={(event) => setGenerateTaxMode(event.target.value as 'skip_existing' | 'replace_existing')}>
                <option value="skip_existing">Skip Existing</option>
                <option value="replace_existing">Replace Existing</option>
              </TaxControlSelect>
            </TaxTwoCol>
            <TaxDonorSearchArea>
              <TaxSearchInputWrap>
                <TaxSearchIcon />
                <TaxSearchInput
                  type="search"
                  placeholder="Search donor for generation"
                  value={generateTaxDonorSearch}
                  onChange={(event) => setGenerateTaxDonorSearch(event.target.value)}
                />
              </TaxSearchInputWrap>
              {showDonorSearchDropdown ? (
                <TaxDonorDropdown>
                  <TaxDonorOptionButton
                    type="button"
                    onClick={() => {
                      setGenerateTaxSelectedDonors([]);
                      setGenerateTaxDonorSearch('');
                    }}
                  >
                    All donors
                  </TaxDonorOptionButton>
                  {generateTaxDonorSearching ? <TaxDonorDropdownLabel>Searching donors...</TaxDonorDropdownLabel> : null}
                  {!generateTaxDonorSearching && generateTaxDonorResults.length === 0 ? (
                    <TaxDonorDropdownLabel>No donors found.</TaxDonorDropdownLabel>
                  ) : null}
                  {!generateTaxDonorSearching
                    ? generateTaxDonorResults.map((option) => (
                        <TaxDonorOptionButton
                          key={option.key}
                          type="button"
                          onClick={() => {
                            setGenerateTaxSelectedDonors((current) => {
                              if (current.some((item) => item.key === option.key)) return current;
                              return [...current, option];
                            });
                            setGenerateTaxDonorSearch('');
                          }}
                        >
                          {option.label}
                        </TaxDonorOptionButton>
                      ))
                    : null}
                </TaxDonorDropdown>
              ) : null}
            </TaxDonorSearchArea>
            <TaxGenerateButton type="button" $primary onClick={handleGenerateTaxStatements} disabled={generatingTaxStatements}>
              {generatingTaxStatements ? 'Generating…' : 'Generate Tax Statements'}
            </TaxGenerateButton>
          </TaxFormStack>
          <GenerationTargetBanner>
            <GenerationTargetLabel>Generation Target</GenerationTargetLabel>
            <GenerationTargetValue>
              {generateTaxSelectedDonors.length > 0
                ? `${generateTaxSelectedDonors.length} selected target${generateTaxSelectedDonors.length === 1 ? '' : 's'}`
                : 'All donors'}
            </GenerationTargetValue>
            <GenerationTargetMeta>
              {generateTaxDonorSearching
                ? 'Searching donors...'
                : generateTaxDonorSearch.trim().length > 0 && generateTaxDonorSearch.trim().length < 2
                ? 'Type at least 2 characters to search donors.'
                : `Year ${generateTaxYear}`}
            </GenerationTargetMeta>
            {generateTaxSelectedDonors.length > 0 ? (
              <GenerationTargetChips>
                {generateTaxSelectedDonors.map((target) => (
                  <GenerationTargetChip
                    key={target.key}
                    type="button"
                    onClick={() => {
                      setGenerateTaxSelectedDonors((current) => current.filter((item) => item.key !== target.key));
                    }}
                    title="Remove target"
                  >
                    {target.label}
                    <GenerationTargetChipRemove>×</GenerationTargetChipRemove>
                  </GenerationTargetChip>
                ))}
              </GenerationTargetChips>
            ) : null}
          </GenerationTargetBanner>

          <TaxBrowseSection>
            <PanelSub style={{ margin: '0 0 0.6rem' }}>
              Search statements for the selected year and download from the list below.
            </PanelSub>
            <TaxBrowseSearchRow>
              <TaxSearchInputWrap>
                <TaxSearchIcon />
                <TaxSearchInput
                  type="search"
                  placeholder="Search statement"
                  value={taxSearch}
                  onChange={(event) => setTaxSearch(event.target.value)}
                />
              </TaxSearchInputWrap>
            </TaxBrowseSearchRow>
          {taxStatements.isLoading ? <HelperText>Loading tax statements…</HelperText> : null}
          {taxStatements.error ? <InlineError>Unable to load tax statements.</InlineError> : null}
          {!taxStatements.isLoading && !taxStatements.error ? (
            <TaxList>
              {filteredTaxStatements.map((statement) => (
                <TaxItem key={statement.id}>
                  <TaxMeta>
                    <TaxName>{statement.display_name || statement.file_name}</TaxName>
                    <TaxSub>
                      {statement.tax_year} - {statement.donor_name || statement.donor_email || 'No donor metadata'}
                    </TaxSub>
                    <TaxSub>
                      Generated {formatDate(statement.generated_at || statement.created_at)}
                    </TaxSub>
                  </TaxMeta>
                  <ActionButton
                    type="button"
                    onClick={() => handleDownloadStatement(statement.id)}
                    disabled={downloadStatementId === statement.id}
                  >
                    <FaDownload size={12} />
                    {downloadStatementId === statement.id ? 'Preparing…' : 'Download'}
                  </ActionButton>
                </TaxItem>
              ))}
              {filteredTaxStatements.length === 0 ? <HelperText>No statements found.</HelperText> : null}
            </TaxList>
          ) : null}
          </TaxBrowseSection>
        </Card>
      </TwoColumn>
    </Page>
  );
}
